/* ============================================================
   NORTHSHEEP — COME BACK TO THE CARD YOU CLICKED

   Owner, 20 Sep 2026: "if someone clicks a product and then go back again
   that they land exactly on the height where they clicked the product instead
   of having to find again where they stopped ... this site kinda seems to
   drop me on a random height or more up or all the way down but not exactly
   where i left off".

   WHY THE BROWSER GETS IT WRONG HERE, AND ONLY HERE
   Restoring a scroll position is the browser's job and it normally does it
   perfectly — measured on this store: leave the grid at 2600px, open a hide,
   press back, and you are returned to the same card at the same 280px from
   the top, zero drift. That is the back-forward cache: the whole live page is
   kept and handed back untouched.

   The drift appears when that cache is NOT used — a phone under memory
   pressure, a reload, a link shared to yourself, iOS evicting the page. Then
   the URL is fetched again, and what comes back is NOT the page that was
   left:

     - the server renders one batch of 48 cards; the other 48 of the visual
       page are fetched by ns-collection-lazy-pages.js only as you scroll,
     - ns-collection-promos.js injects promo tiles into the grid,
     - ns-collection-curate.js re-applies the saved custom order and hides
       blocked products.

   So the browser restores a scroll offset of, say, 4200px into a document
   that is currently 2000px tall. It clamps to the bottom. Then the missing
   cards arrive and the ground moves again. "More up, or all the way down, but
   not where I left off" is exactly what that produces.

   THE ANSWER IS NOT A PIXEL OFFSET
   A remembered scrollY describes a document that no longer exists at the
   moment it is applied. A remembered CARD does not: it is the same hide
   wherever it ends up on the page. So this records which card was clicked and
   how far down the screen it was sitting, then puts that card back in that
   spot — loading more batches first if the card has not arrived yet, and
   re-checking as promos and ordering shift things underneath.

   This file does nothing when the page comes back from the back-forward
   cache (pageshow.persisted) — recording is all it ever does.

   That sentence used to end "...the case that already works", and it was
   wrong for months. Measured on iOS Safari, 21 Sep 2026: the page comes back
   alive and lands on the header, because Safari restores the DOCUMENT but not
   the OFFSET. The restore for it lives in ns-grid-restore.js, in a pageshow
   listener, because a cached page never runs its scripts again — see the long
   note there.
   ============================================================ */
(function () {
  'use strict';
  if (window.__nsGridReturn) return;
  window.__nsGridReturn = true;

  var KEY = 'northsheep_grid_return';
  /* An hour. Long enough for "I went to look at something and came back",
     short enough that a position from this morning does not hijack a fresh
     visit this afternoon. */
  var MAX_AGE = 60 * 60 * 1000;
  /* A return through a link rather than the back button — a breadcrumb, the
     store's own "back to collection" — only counts if it happened just now. */
  var FRESH_NAV = 2 * 60 * 1000;

  var SS = (function () {
    try {
      sessionStorage.setItem('__nsgr__', '1');
      sessionStorage.removeItem('__nsgr__');
      return true;
    } catch (e) {
      return false;
    }
  })();
  if (!SS) return;

  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  /* ONE RECORD PER VIEW AS WELL (audit, 4 Oct 2026, S5). A collection reached
     from another collection - the mini-cowhide window card, a promo tile, a
     colour pill that opens its own collection - wrote its own record over the
     first one, and Back to the first landed at the top. The newest record is
     still the one above (every reader keeps working unchanged); a copy per
     view (path + query) is kept here, and the head of theme.liquid and
     ns-grid-restore.js take a view's own copy when the newest is another
     view's. Eight views, an hour each: the same limits as the record. */
  var VIEWS_KEY = 'northsheep_grid_returns';
  function write(v) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(v));
    } catch (e) {}
    try {
      var all = JSON.parse(sessionStorage.getItem(VIEWS_KEY) || '{}') || {};
      all[v.url] = v;
      var urls = Object.keys(all).filter(function (u) {
        return all[u] && Date.now() - all[u].ts < MAX_AGE;
      });
      urls.sort(function (a, b) {
        return all[b].ts - all[a].ts;
      });
      var keep = {};
      for (var i = 0; i < urls.length && i < 8; i++) keep[urls[i]] = all[urls[i]];
      sessionStorage.setItem(VIEWS_KEY, JSON.stringify(keep));
    } catch (e) {}
  }
  function clear() {
    try {
      sessionStorage.removeItem(KEY);
    } catch (e) {}
  }

  /* The view, not just the collection: page 3 of a sorted list is a different
     list from page 1 of the default one, and putting a card back in the wrong
     one would be worse than not trying. */
  function here() {
    return location.pathname + location.search;
  }
  function gridEl() {
    return document.getElementById('product-grid');
  }
  function cardOf(node) {
    if (!node || !node.closest) return null;
    var card = node.closest('[data-ns-card-handle]');
    /* A link in the enlarged card (ns-card-look.js, 1 Oct 2026): the card is
       lifted out of the grid for the moment, and the place it keeps there
       (.ns-look-slot) is the card to come back to. */
    if (!card && node.closest('.ns-look__frame')) card = document.querySelector('#product-grid .ns-look-slot[data-ns-card-handle]');
    return card;
  }

  /* The ordered handles of the grid as it stands, minus the promo tiles and
     minus anything the shopper has hidden. Read by ns-product-pager.js.

     Kept in its own storage entry, not folded into the one above: the
     position entry is consumed the moment it is used to put the page back,
     while the list has to survive being walked through a dozen hides. */
  var LIST_KEY = 'northsheep_grid_list';
  function writeList(clicked) {
    var g = gridEl();
    if (!g) return;
    var nodes = g.querySelectorAll('[data-ns-card-handle]');
    var handles = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].classList.contains('ns-curate-hidden')) continue;
      var h = nodes[i].getAttribute('data-ns-card-handle');
      if (h && handles.indexOf(h) === -1) handles.push(h);
    }
    if (handles.length < 2) return;
    /* HOW FAR INTO THE COLLECTION THIS PAGE STARTS (owner, 20 Sep 2026: "i
       noticed it ignores pagination so then it just says 3 again for example
       instead of 97").

       The list only ever holds the page the shopper is on — 96 cards, not the
       whole 256 — so the position inside it starts again at 1 on every page.
       The products before it have to be counted back in, or the third hide on
       page two is announced as the third hide in the collection.

       Liquid stamps both halves on the grid: the visual page's first Shopify
       page, and how many products a Shopify page holds. */
    var pageSize = parseInt(g.getAttribute('data-ns-page-size'), 10) || 0;
    var curPage = parseInt(g.getAttribute('data-ns-current-page'), 10) || 1;
    var offset = pageSize > 0 ? (curPage - 1) * pageSize : 0;
    var title = document.querySelector('.collection-hero__title, .ns-collection-hero__title, h1');
    try {
      sessionStorage.setItem(
        LIST_KEY,
        JSON.stringify({
          url: here(),
          /* The list is only part of the collection — the part that has been
             loaded. The pager needs the real total so "12 of 256" is honest
             rather than "12 of 96". */
          total: parseInt(g.getAttribute('data-ns-total-items'), 10) || handles.length,
          offset: offset,
          /* A client-side filter (favourites, size, hidden) rearranges the
             grid without touching the URL, so neither the offset nor the
             collection's total describes what the shopper is looking at any
             more. Said plainly here so the pager can count the list itself
             instead of lying about the collection. */
          /* body.ns-client-filter-on is set by ns-collection-filters.js whenever
             its own filter has the grid (audit, 2 Oct 2026: the classes tested
             before were never set for Size, Sale or a colour pill, so the pager
             said "3 of 361" under them). The old tests stay as a fallback. */
          filtered: document.body.classList.contains('ns-client-filter-on') || document.body.className.indexOf('ns-fav-filter-on') > -1 || !!document.querySelector('[data-ns-size-filter].is-active, .ns-fav-filter--active, body.ns-show-hidden'),
          title: title ? title.textContent.trim().slice(0, 80) : '',
          handles: handles,
          from: clicked ? clicked.getAttribute('data-ns-card-handle') : '',
          ts: Date.now()
        })
      );
    } catch (e) {}
  }

  /* ---------------- remember ---------------- */

  /* The click is the best moment: it says exactly which card the shopper
     chose, and where it was on their screen at that instant. */
  document.addEventListener(
    'click',
    function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      /* A card can also lead to a collection: the mini-cowhide window card
         (audit, 4 Oct 2026, S5). Its place is recorded the same way; only a
         product starts the product pager's list. */
      var toProduct = a.href.indexOf('/products/') !== -1;
      if (!toProduct && a.href.indexOf('/collections/') === -1) return;
      var card = cardOf(a);
      if (!card || !gridEl() || !gridEl().contains(card)) return;
      var handle = card.getAttribute('data-ns-card-handle');
      if (!handle) return;
      write({
        url: here(),
        handle: handle,
        /* Where the card sat in the viewport. Restoring to the same offset
           puts the page back exactly as it looked, rather than dumping the
           card at the top of the screen. */
        top: Math.round(card.getBoundingClientRect().top),
        /* A PROVISIONAL ANSWER FOR THE MOMENT THE CARD DOES NOT EXIST YET
           (owner, 20 Sep 2026: "i dont fluently continue where i was but
           first i jump up and down before jumping to the correct spot").

           If the clicked hide was in the second batch, the card cannot be
           placed until that batch arrives — so the page used to sit wherever
           the browser had clamped it and then leap into position. The raw
           offset is not a good final answer (the document changes underneath
           it), but it is a very good FIRST one now that ns-grid-fade.js holds
           the missing rows open: the page is already its full height, so this
           lands within a row of the truth and the real placement that follows
           is a nudge rather than a jump. */
        y: Math.round(window.scrollY),
        ts: Date.now()
      });
      /* THE RACK THEY WERE FLIPPING, recorded at the same moment for the
         previous/next arrows on the product page (ns-product-pager.js).

         It has to be taken HERE rather than rebuilt there, because this list
         is not "the collection" — it is the collection as this shopper had it
         arranged: their sort, their filters, their hidden products gone,
         their custom order applied. Rebuilding it from the collection later
         would hand them back hides they had already dismissed. */
      if (toProduct) writeList(card);
    },
    true
  );

  /* A safety net for every other way of leaving: the header, a share sheet, a
     swipe back from the browser chrome. The topmost card fully in view is a
     good enough answer, and it is only used if no click was recorded for this
     view. */
  window.addEventListener(
    'pagehide',
    function () {
      var g = gridEl();
      if (!g) return;
      var prev = read();
      if (prev && prev.url === here() && Date.now() - prev.ts < 4000) {
        /* A CLICK JUST RECORDED IT — BUT AT THE TAP, NOT AT THE LEAVING
           (owner's iPhone, 23 Sep 2026: "it still jumps down a bit on mobile
           occasionally").

           Measured on that phone: the browser put the page back at 11505,
           this record said 11475, and the two took turns — 11505, 11475,
           11505, 11475. Chrome on an iPhone brings its toolbar back when a
           link is tapped and shifts the page by it AFTER the tap; the browser
           saves the page as it is when it is LEFT, which is now. So the
           clicked card is kept (the product pager needs it) and its position
           is re-read here, the moment the browser takes its own note. */
        var h = prev.handle ? String(prev.handle).replace(/"/g, '\\"') : '';
        var card = h ? g.querySelector('[data-ns-card-handle="' + h + '"]') : null;
        prev.y = Math.round(window.scrollY);
        if (card) prev.top = Math.round(card.getBoundingClientRect().top);
        write(prev);
        return;
      }
      var cards = g.querySelectorAll('[data-ns-card-handle]');
      for (var i = 0; i < cards.length; i++) {
        var r = cards[i].getBoundingClientRect();
        if (r.bottom > 0 && r.top > -r.height / 2) {
          write({
            url: here(),
            handle: cards[i].getAttribute('data-ns-card-handle'),
            top: Math.round(r.top),
            y: Math.round(window.scrollY),
            ts: Date.now()
          });
          return;
        }
      }
    },
    false
  );

  /* ---------------- putting it back is NOT here ----------------

     It used to be: a restore() with its own placement loop, a heartbeat, a
     grid observer, a seed, a soft path for the back-forward cache and a
     giveUp. Together with the head script's hold, the height claim and the
     spacer in ns-grid-fade.js, that made five separate things writing the
     scroll position on five schedules — and every repair to one disturbed
     another.

     All of it now lives in assets/ns-grid-restore.js, which does the one
     thing that works: anchor to the CARD, every tick, until the page stops
     moving. This file only REMEMBERS — which hide was clicked, where it sat,
     and the rack the shopper was flipping through. Recording and restoring
     are different jobs and they no longer share a file. */
})();
